import { onAnimationEnd } from '@theme/utilities';

class SkreyHeaderDrawer extends HTMLElement {
  #abortController;
  #activeTabHandle = null;
  #returnFocus = null;
  #ownsScrollLock = false;
  #closeRequestId = 0;
  #desktopMediaQuery = window.matchMedia('(min-width: 990px)');

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.#desktopMediaQuery.addEventListener('change', this.#syncResponsiveDetails, { signal });
    this.#syncResponsiveDetails();

    const defaultTab = this.dataset.defaultTab;
    if (defaultTab) {
      this.#activeTabHandle = defaultTab;
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#close(false);
  }

  #syncResponsiveDetails = () => {
    const details = this.querySelector('.skrey-mobile-drawer > .menu-drawer-container');
    if (!(details instanceof HTMLDetailsElement)) return;

    this.#close(false);
    details.classList.remove('menu-open');

    if (this.#desktopMediaQuery.matches) {
      details.removeAttribute('scroll-lock');
      details.open = true;
    } else {
      details.open = false;
      details.setAttribute('scroll-lock', '');
    }

    document.documentElement.removeAttribute('scroll-lock');
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const desktopTrigger = event.target.closest('[data-skrey-drawer-trigger]');
    if (desktopTrigger && this.contains(desktopTrigger) && this.#desktopMediaQuery.matches) {
      event.preventDefault();
      const handle = desktopTrigger.dataset.skreyTab;
      if (!handle) return;

      if (this.dataset.drawerOpen === 'true' && this.#activeTabHandle === handle) {
        this.#close();
      } else {
        this.#openTab(handle, desktopTrigger);
      }
      return;
    }

    const mobileTab = event.target.closest('[data-skrey-mobile-tab]');
    if (mobileTab && this.contains(mobileTab)) {
      event.preventDefault();
      const handle = mobileTab.dataset.skreyMobileTab;
      if (handle) this.#switchTab(handle);
      return;
    }

    const closeButton = event.target.closest('[data-skrey-drawer-close], [data-skrey-drawer-backdrop]');
    if (closeButton && this.contains(closeButton)) {
      event.preventDefault();
      this.#close();
    }
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.dataset.drawerOpen === 'true') {
      event.preventDefault();
      this.#close();
    }
  };

  #onPointerDown = (event) => {
    if (event.target instanceof Element && event.target.closest('.skrey-mobile-drawer .header__icon--summary')) {
      this.#updateDrawerTop();
    }

    if (this.dataset.drawerOpen !== 'true' || !(event.target instanceof Node)) return;

    if (
      event.target instanceof Element &&
      event.target.closest('[data-skrey-drawer-close], [data-skrey-drawer-backdrop], [data-skrey-drawer-trigger]')
    ) {
      return;
    }

    const panel = this.querySelector('[data-skrey-drawer-panel]');
    if (panel?.contains(event.target)) return;

    this.#close();
  };

  #openTab(handle, trigger) {
    this.#closeRequestId++;
    const requestId = this.#closeRequestId;

    this.#updateDrawerTop();
    this.#switchTab(handle);

    this.#returnFocus = trigger instanceof HTMLElement ? trigger : null;

    const panel = this.querySelector('[data-skrey-drawer-panel]');
    const backdrop = this.querySelector('[data-skrey-drawer-backdrop]');

    if (panel instanceof HTMLElement) panel.hidden = false;
    if (backdrop instanceof HTMLElement) backdrop.hidden = false;

    const headerClose = this.querySelector('.skrey-desktop-drawer__close--header');
    if (headerClose instanceof HTMLElement) {
      headerClose.setAttribute('aria-hidden', 'false');
      headerClose.removeAttribute('tabindex');
    }

    // Paint the closed visual state first (opacity 0 / off-canvas), then open on
    // the next frame so CSS transitions run. Apply scroll-lock after open state
    // so sticky reflow does not race the transition start.
    requestAnimationFrame(() => {
      if (requestId !== this.#closeRequestId) return;

      this.dataset.drawerOpen = 'true';
      this.#syncDesktopTriggers(handle);

      document.documentElement.setAttribute('scroll-lock', '');
      this.#ownsScrollLock = true;

      const activePanel = this.querySelector(`[data-skrey-tab-panel="${handle}"]:not([hidden])`);
      const firstFocusable = activePanel?.querySelector('a[href], summary, button:not([disabled])');
      if (firstFocusable instanceof HTMLElement) firstFocusable.focus({ preventScroll: true });
    });
  }

  #switchTab(handle) {
    this.#activeTabHandle = handle;

    this.querySelectorAll('[data-skrey-tab-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const isActive = panel.dataset.skreyTabPanel === handle;
      panel.hidden = !isActive;
      panel.classList.toggle('skrey-drawer-tab-panel--active', isActive);
    });

    this.querySelectorAll('[data-skrey-mobile-tab]').forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;

      const isSelected = tab.dataset.skreyMobileTab === handle;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.classList.toggle('skrey-drawer-tabs-list__tab--active', isSelected);
    });

    if (this.#desktopMediaQuery.matches) {
      this.#syncDesktopTriggers(handle);
    }
  }

  #syncDesktopTriggers(activeHandle) {
    this.querySelectorAll('[data-skrey-drawer-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;

      const isActive = trigger.dataset.skreyTab === activeHandle;
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }

  #clearDesktopTriggers() {
    this.querySelectorAll('[data-skrey-drawer-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  async #close(restoreFocus = true) {
    this.#closeRequestId++;
    const requestId = this.#closeRequestId;

    const panel = this.querySelector('[data-skrey-drawer-panel]');
    const backdrop = this.querySelector('[data-skrey-drawer-backdrop]');

    const shouldAnimateClose =
      restoreFocus &&
      this.dataset.drawerOpen === 'true' &&
      this.#desktopMediaQuery.matches &&
      panel instanceof HTMLElement &&
      backdrop instanceof HTMLElement &&
      !panel.hidden;

    if (shouldAnimateClose) {
      this.removeAttribute('data-drawer-open');
      this.#clearDesktopTriggers();
      await onAnimationEnd([panel, backdrop], undefined, { subtree: false });
    }

    if (requestId !== this.#closeRequestId) return;

    if (panel instanceof HTMLElement) panel.hidden = true;
    if (backdrop instanceof HTMLElement) backdrop.hidden = true;

    this.#clearDesktopTriggers();

    const headerClose = this.querySelector('.skrey-desktop-drawer__close--header');
    if (headerClose instanceof HTMLElement) {
      headerClose.setAttribute('aria-hidden', 'true');
      headerClose.setAttribute('tabindex', '-1');
    }

    this.removeAttribute('data-drawer-open');

    if (this.#ownsScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
      this.#ownsScrollLock = false;
    }

    if (restoreFocus && this.#returnFocus?.isConnected) {
      this.#returnFocus.focus({ preventScroll: true });
    }
    this.#returnFocus = null;
  }

  #updateDrawerTop() {
    const header = this.closest('#header-component');
    if (!(header instanceof HTMLElement)) return;

    const drawerTop = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    header.style.setProperty('--skrey-drawer-top', `${drawerTop}px`);
  }
}

if (!customElements.get('skrey-header-drawer')) {
  customElements.define('skrey-header-drawer', SkreyHeaderDrawer);
}
